import React, { FC } from 'react';
import { DynamicComponent, Errors } from '@/components';
import { TypeAccountPage, TypeEmptyDirective } from '@/core/generated-types';
import {
  ErrorDirectiveProvider,
  useErrorDirectives,
} from '@/context/ErrorDirectiveContext';
import { ERROR_KINDS } from '@/core/constants';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { PageDataProvider } from '@/context/PageDataProvider';
import { TypeSubscriberShopFamilyPageData } from '@/pages/[locale]/account/subscriber-shop/family';
import { TypeSubscriberShopPageData } from '@/pages/[locale]/account/subscriber-shop';
import { TypeReplacementPartsProductsPageData } from '@/pages/[locale]/account/replacement-parts';
import { TypeReplacementPartsComponentsPageData } from '@/pages/[locale]/account/replacement-parts/[product]';
import { TypeReplacementPartsSubcomponentsPageData } from '@/pages/[locale]/account/replacement-parts/[product]/[component]';
import { Empty } from '@/components/common/Empty';
import {
  EmptyDirectiveProvider,
  useEmptyDirective,
} from '@/context/EmptyDirectiveContext';
import AdminPreviewNotification, {
  AdminPreviewNotificationProps,
} from '@/components/layouts/account/AdminPreviewNotification';

export type SiblingsPageLayoutProps = {
  page: TypeAccountPage;
  // introduce union type for new page data options as they arise
  data?:
    | TypeSubscriberShopFamilyPageData
    | TypeSubscriberShopPageData
    | TypeReplacementPartsProductsPageData
    | TypeReplacementPartsComponentsPageData
    | TypeReplacementPartsSubcomponentsPageData;
  isError?: boolean;
  isEmpty?: boolean;
  adminPreview?: AdminPreviewNotificationProps | undefined;
};

export const PageComponents: FC<SiblingsPageLayoutProps> = ({
  page,
  data,
  isError,
  isEmpty,
}) => {
  const {
    fields: { components },
  } = page;

  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );
  const { emptyDirective } = useEmptyDirective();

  if (isError) {
    return <Errors errors={errors} heading={heading} />;
  }

  if (isEmpty) {
    return <Empty {...emptyDirective.fields} />;
  }

  return (
    <section>
      {components &&
        components.map((component) => (
          <DynamicComponent
            key={component.sys.id}
            component={component}
            data={data}
          />
        ))}
    </section>
  );
};

const SiblingsPageLayout: FC<SiblingsPageLayoutProps> = ({
  page,
  isError,
  isEmpty,
  data,
  adminPreview,
}) => {
  return (
    <PageDataProvider data={data}>
      <ErrorDirectiveProvider errorDirectives={page.fields.errorDirectives}>
        <EmptyDirectiveProvider
          emptyDirective={page.fields.emptyDirective as TypeEmptyDirective}
        >
          {adminPreview && (
            <div className="container mx-auto my-4">
              <AdminPreviewNotification
                adminName={adminPreview?.adminName}
                userEmail={adminPreview?.userEmail}
              />
            </div>
          )}
          <PageComponents
            page={page}
            isError={!!isError}
            isEmpty={!!isEmpty}
            data={data}
          />
        </EmptyDirectiveProvider>
      </ErrorDirectiveProvider>
    </PageDataProvider>
  );
};

export default SiblingsPageLayout;
